/**
 * Résultats et devenir (lot 4, GO de Daniel du 01/11/2026).
 *
 * Pour une année d'examen : le taux de réussite de l'école à chaque examen,
 * ce que sont devenus les admis et les ajournés l'année suivante (restés chez
 * nous, partis), et le taux de réinscription réel, hors bacheliers - l'école
 * s'arrête au lycée, un bachelier qui part n'est pas une perte.
 *
 * L'année se choisit ici : en novembre, on regarde les examens de juin, donc
 * l'année d'avant, quelle que soit l'année affichée dans l'en-tête.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GraduationCap } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import ChampSelect from '../ui/ChampSelect';
import EtatVide from '../ui/EtatVide';
import LoadingSpinner from '../LoadingSpinner';
import FilArianeExamen from './FilArianeExamen';
import { EXAMENS_NATIONAUX } from '../../config/examensConfig';
import { libelleMention } from './ResultatsExamen';
import { api } from '../../services/api';

const LIBELLES_RESULTAT = { admis: 'Admis', ajourne: 'Ajourné', absent: 'Absent' };
const TEINTES_RESULTAT = {
  admis: 'bg-green-100 text-green-800',
  ajourne: 'bg-red-100 text-red-800',
  absent: 'bg-gray-200 text-gray-700'
};
const LIBELLES_DEVENIR = {
  reste: 'Resté',
  parti: 'Parti',
  parti_apres_reinscription: 'Réinscrit puis parti',
  en_attente: 'Réinscription en attente'
};
const TEINTES_DEVENIR = {
  reste: 'bg-[#eef7fb] text-[#246990]',
  parti: 'bg-amber-50 text-amber-800',
  parti_apres_reinscription: 'bg-amber-50 text-amber-800',
  en_attente: 'bg-gray-100 text-gray-700'
};

// Un élève resté passe en classe supérieure ou redouble : lu par le serveur en
// comparant le niveau de ses deux classes (DevenirResultatsService,
// niveauDeClasse). Sans niveau lisible dans le nom, il reste « Resté ».
const LIBELLES_PARCOURS = { passe: 'Passe', redouble: 'Redouble' };
const TEINTES_PARCOURS = {
  passe: 'bg-[#eef7fb] text-[#246990]',
  redouble: 'bg-sky-50 text-sky-800'
};

const FILTRES_DEVENIR = [
  { valeur: 'tous', libelle: 'Tous les devenirs' },
  { valeur: 'reste', libelle: 'Restés' },
  { valeur: 'passe', libelle: 'Passent en classe supérieure' },
  { valeur: 'redouble', libelle: 'Redoublent' },
  { valeur: 'partis', libelle: 'Partis' },
  { valeur: 'en_attente', libelle: 'Réinscription en attente' }
];
const FILTRES_RESULTAT = [
  { valeur: 'tous', libelle: 'Tous les résultats' },
  { valeur: 'admis', libelle: 'Admis' },
  { valeur: 'ajourne', libelle: 'Ajournés' },
  { valeur: 'absent', libelle: 'Absents' }
];

const formaterTaux = (taux) => (taux === null || taux === undefined
  ? '-'
  : `${Number(taux).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);

// Les couleurs disent le sens, comme dans l'import (recette de Daniel du
// 01/11/2026) : vert les admis, rouge les ajournés, gris les absents, bleu
// ciel les bacheliers qui sortent, bleu KLASYS le chiffre que l'école retient.
// Teintes douces seulement : le bleu plein reste aux boutons.
const TONS_CARTE = {
  neutre: { carte: 'border-gray-200 bg-gray-50', valeur: 'text-gray-900' },
  admis: { carte: 'border-green-200 bg-green-50', valeur: 'text-green-800' },
  ajourne: { carte: 'border-red-200 bg-red-50', valeur: 'text-red-800' },
  absent: { carte: 'border-gray-200 bg-gray-100', valeur: 'text-gray-700' },
  sortant: { carte: 'border-sky-200 bg-sky-50', valeur: 'text-sky-800' }
};

const CarteChiffre = ({ libelle, valeur, note = null, fort = false, ton = 'neutre' }) => {
  const t = TONS_CARTE[ton] || TONS_CARTE.neutre;
  return (
    <div className={`rounded-md border p-3 ${fort ? 'border-[#246990]/20 bg-[#eef7fb]' : t.carte}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{libelle}</p>
      <p className={`mt-1 tabular-nums ${fort ? 'text-2xl font-bold text-[#246990]' : `text-lg font-semibold ${t.valeur}`}`}>{valeur}</p>
      {note && <p className="text-[11px] text-gray-500">{note}</p>}
    </div>
  );
};

// Un bandeau par bloc, chaque examen dans sa teinte : sur une page longue,
// on repère d'un coup d'œil où commence le BEPC. Trois teintes froides et
// douces, qui s'accordent entre elles et avec le bleu KLASYS de la réinscription.
const TONS_BLOC = {
  reinscription: { bande: 'bg-[#eef7fb] border-[#246990]/20', titre: 'text-[#246990]', puce: 'bg-[#246990]' },
  CEPE: { bande: 'bg-teal-50 border-teal-200', titre: 'text-teal-900', puce: 'bg-teal-500' },
  BEPC: { bande: 'bg-sky-50 border-sky-200', titre: 'text-sky-900', puce: 'bg-sky-500' },
  BAC: { bande: 'bg-indigo-50 border-indigo-200', titre: 'text-indigo-900', puce: 'bg-indigo-500' }
};

// Les colonnes du devenir, teintées comme leur sens.
const TEINTES_COLONNES = {
  reste: 'bg-green-50 text-green-800',
  parti: 'bg-amber-50 text-amber-800',
  parti_apres_reinscription: 'bg-amber-50/60 text-amber-800',
  en_attente: 'bg-gray-50 text-gray-600'
};

const Badge = ({ libelle, teinte }) => (
  <span className={`inline-flex h-6 min-w-[96px] items-center justify-center rounded-lg px-2 text-[10px] font-semibold ${teinte}`}>
    {libelle}
  </span>
);

const Th = ({ children, className = '', teinte = 'text-gray-500' }) => (
  <th className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide ${teinte} ${className}`}>
    {children}
  </th>
);

/**
 * Les admis au dernier examen de l'école (paramétrage, BAC par défaut) : les
 * « bacheliers » quand l'école va jusqu'au lycée, les admis au BEPC quand
 * elle s'arrête au collège.
 */
const nommerSortants = (examenFinal) => {
  const libelle = EXAMENS_NATIONAUX[examenFinal]?.libelleLong || examenFinal;
  return examenFinal === 'BAC'
    ? { titre: 'Bacheliers', groupe: 'les bacheliers', court: 'bacheliers', examen: libelle }
    : { titre: `Admis au ${libelle}`, groupe: `les admis au ${libelle}`, court: `admis au ${libelle}`, examen: libelle };
};

/** Le bloc de la réinscription : taux actuel, sortants naturels, taux réel. */
const BlocReinscription = ({ reinscription, annee, suivante, examenFinal = 'BAC' }) => {
  if (!suivante) {
    return (
      <div className="mb-6 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 text-sm text-gray-700">
        L&apos;année qui suit {annee.nom} n&apos;existe pas encore : le devenir des candidats et le taux de
        réinscription se liront quand elle sera créée et que les réinscriptions auront commencé.
      </div>
    );
  }
  const ton = TONS_BLOC.reinscription;
  const sortants = nommerSortants(examenFinal);
  return (
    <Card className="mb-6 overflow-hidden">
      <div className={`border-b px-5 py-3 ${ton.bande}`}>
        <p className={`flex items-center gap-2 text-base font-bold ${ton.titre}`}>
          <span className={`inline-block h-2.5 w-2.5 rounded-sm ${ton.puce}`} />
          Réinscription {annee.nom} vers {suivante.nom}
        </p>
        <p className="mt-0.5 text-xs text-gray-600">
          {sortants.groupe.charAt(0).toUpperCase() + sortants.groupe.slice(1)} partent par nature : le taux réel les retire des deux côtés.
        </p>
      </div>
      <CardContent className="grid grid-cols-1 gap-3 p-5 md:grid-cols-3">
        <CarteChiffre
          libelle="Taux de réinscription actuel"
          valeur={formaterTaux(reinscription.taux_brut)}
          note={`${reinscription.reinscrits} réinscrits sur ${reinscription.eleves} élèves - celui des Admissions`}
        />
        <CarteChiffre
          libelle={sortants.titre}
          valeur={reinscription.bacheliers}
          ton="sortant"
          note={`Admis au ${sortants.examen}, dernier examen de l'école : ils la quittent par nature`}
        />
        <CarteChiffre
          libelle={`Taux réel, hors ${sortants.court}`}
          valeur={formaterTaux(reinscription.taux_reel)}
          fort
          note={reinscription.taux_reel === null
            ? `Saisissez les résultats du ${sortants.examen} pour le calculer`
            : `${reinscription.reinscrits_hors_bacheliers} réinscrits sur ${reinscription.base_reelle} élèves qui pouvaient rester`}
        />
      </CardContent>
    </Card>
  );
};

/** Un examen : synthèse, tableau du devenir, candidats libres, liste des élèves. */
const BlocExamen = ({ examen, suivante, examenFinal = 'BAC' }) => {
  const config = EXAMENS_NATIONAUX[examen.code];
  const { synthese, devenir, candidats_libres: libres } = examen;
  const [voirEleves, setVoirEleves] = useState(false);
  const [filtreDevenir, setFiltreDevenir] = useState('tous');
  const [filtreResultat, setFiltreResultat] = useState('tous');

  const eleves = useMemo(() => examen.eleves.filter((e) => {
    if (filtreResultat !== 'tous' && e.resultat !== filtreResultat) return false;
    if (filtreDevenir === 'partis') return e.devenir === 'parti' || e.devenir === 'parti_apres_reinscription';
    if (filtreDevenir === 'passe' || filtreDevenir === 'redouble') return e.parcours === filtreDevenir;
    if (filtreDevenir !== 'tous') return e.devenir === filtreDevenir;
    return true;
  }), [examen.eleves, filtreDevenir, filtreResultat]);

  const aucunResultat = synthese.presents + synthese.absents === 0;
  // Un bachelier qui part a fini son cycle : ce n'est pas un départ à expliquer.
  // Le dernier examen de l'école se règle au paramétrage (BAC par défaut).
  const finDeCycle = (resultat) => examen.code === examenFinal && resultat === 'admis';

  return (
    <Card className="mb-6 overflow-hidden">
      <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3 ${(TONS_BLOC[examen.code] || TONS_BLOC.reinscription).bande}`}>
        <div>
          <p className={`flex items-center gap-2 text-base font-bold ${(TONS_BLOC[examen.code] || TONS_BLOC.reinscription).titre}`}>
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${(TONS_BLOC[examen.code] || TONS_BLOC.reinscription).puce}`} />
            {config?.libelleLong || examen.code}
          </p>
          <p className="text-xs text-gray-600">{config?.nomComplet} - classes de {config?.libelleClasses}</p>
        </div>
        {!aucunResultat && (
          <Button variant="outline" size="sm" onClick={() => setVoirEleves((v) => !v)}>
            {voirEleves ? 'Masquer les élèves' : 'Voir les élèves'}
          </Button>
        )}
      </div>

      <CardContent className="space-y-5 p-5">
        {aucunResultat ? (
          <p className="text-sm text-gray-500">
            Aucun résultat saisi pour cet examen. Ouvrez la liste du {config?.libelleLong}, vue Résultats,
            pour les saisir ou importer la liste officielle.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <CarteChiffre libelle="Candidats" valeur={synthese.eleves} />
              <CarteChiffre libelle="Admis" valeur={synthese.admis} ton="admis" />
              <CarteChiffre libelle="Ajournés" valeur={synthese.ajournes} ton="ajourne" />
              <CarteChiffre libelle="Absents" valeur={synthese.absents} ton="absent" />
              <CarteChiffre libelle="Taux de réussite" valeur={formaterTaux(synthese.taux_reussite)} fort note="Admis sur présents" />
            </div>

            {devenir && (
              <div>
                <p className="mb-2 text-sm font-semibold text-gray-900">Devenir en {suivante.nom}</p>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <Th>Résultat</Th>
                        <Th className="text-center">Total</Th>
                        <Th className="text-center" teinte={TEINTES_COLONNES.reste}>Restés</Th>
                        <Th className="text-center" teinte={TEINTES_COLONNES.parti}>Partis</Th>
                        <Th className="text-center" teinte={TEINTES_COLONNES.parti_apres_reinscription}>Réinscrits puis partis</Th>
                        <Th className="text-center" teinte={TEINTES_COLONNES.en_attente}>En attente</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {['admis', 'ajourne', 'absent'].filter((r) => devenir[r].total > 0).map((r) => {
                        const ligne = devenir[r];
                        return (
                          <tr key={r}>
                            <td className="px-4 py-3"><Badge libelle={LIBELLES_RESULTAT[r]} teinte={TEINTES_RESULTAT[r]} /></td>
                            <td className="px-4 py-3 text-center text-sm font-semibold tabular-nums">{ligne.total}</td>
                            <td className="px-4 py-3 text-center text-sm font-semibold tabular-nums text-green-800">
                              {ligne.reste}
                              {(ligne.passe > 0 || ligne.redouble > 0) && (
                                <span className="block text-[11px] text-gray-500">
                                  {[
                                    ligne.passe > 0 && `${ligne.passe} passe${ligne.passe > 1 ? 'nt' : ''}`,
                                    ligne.redouble > 0 && `${ligne.redouble} redouble${ligne.redouble > 1 ? 'nt' : ''}`
                                  ].filter(Boolean).join(', ')}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-sm tabular-nums">
                              {ligne.parti}
                              {finDeCycle(r) && <span className="block text-[11px] text-gray-500">fin de cycle</span>}
                            </td>
                            <td className="px-4 py-3 text-center text-sm tabular-nums">{ligne.parti_apres_reinscription}</td>
                            <td className="px-4 py-3 text-center text-sm tabular-nums">{ligne.en_attente}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {libres.eleves > 0 && (
          <p className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm text-violet-900">
            <span className="font-semibold">Candidats libres :</span>
            {' '}
            {libres.admis} admis sur {libres.presents} présent{libres.presents > 1 ? 's' : ''}
            {libres.taux_reussite !== null && ` (${formaterTaux(libres.taux_reussite)})`}
            {libres.non_renseignes > 0 && `, ${libres.non_renseignes} sans résultat`}
            {' - hors du taux de réussite de l\'école.'}
          </p>
        )}

        {voirEleves && !aucunResultat && (
          <div>
            <div className="mb-3 flex flex-wrap gap-2">
              <ChampSelect valeur={filtreResultat} onChange={setFiltreResultat} options={FILTRES_RESULTAT} className="w-52" />
              {suivante && (
                <ChampSelect valeur={filtreDevenir} onChange={setFiltreDevenir} options={FILTRES_DEVENIR} className="w-60" />
              )}
              <span className="self-center text-xs text-gray-500">
                {eleves.length} élève{eleves.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="max-h-[520px] overflow-auto rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-50 border-b">
                  <tr>
                    <Th>Élève</Th>
                    <Th>Classe</Th>
                    <Th className="text-center">Résultat</Th>
                    <Th className="text-center">Mention</Th>
                    {suivante && <Th className="text-center">Devenir</Th>}
                    {suivante && <Th>Classe en {suivante.nom}</Th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {eleves.map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <p className="text-sm font-medium text-gray-900">{e.nom} {e.prenom}</p>
                        <p className="text-[11px] text-gray-500">{e.matricule}</p>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{e.classe_nom}</td>
                      <td className="px-4 py-2 text-center">
                        <Badge libelle={LIBELLES_RESULTAT[e.resultat]} teinte={TEINTES_RESULTAT[e.resultat]} />
                      </td>
                      <td className="px-4 py-2 text-center text-sm text-gray-700">{libelleMention(e.mention) || '-'}</td>
                      {suivante && (
                        <td className="px-4 py-2 text-center">
                          {e.devenir === 'parti' && finDeCycle(e.resultat)
                            ? <Badge libelle="Fin de cycle" teinte="bg-gray-100 text-gray-700" />
                            : e.devenir === 'reste' && e.parcours
                              ? <Badge libelle={LIBELLES_PARCOURS[e.parcours]} teinte={TEINTES_PARCOURS[e.parcours]} />
                              : e.devenir && <Badge libelle={LIBELLES_DEVENIR[e.devenir]} teinte={TEINTES_DEVENIR[e.devenir]} />}
                        </td>
                      )}
                      {suivante && <td className="px-4 py-2 text-sm text-gray-700">{e.classe_suivante || '-'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ResultatsDevenir = ({ onRetour, onChangerExamen }) => {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [echec, setEchec] = useState(null);
  const [anneeChoisie, setAnneeChoisie] = useState(null);

  const charger = useCallback(async (anneeId) => {
    setChargement(true);
    setEchec(null);
    try {
      const reponse = await api.examens.getDevenirResultats(anneeId);
      if (!reponse?.success) throw new Error(reponse?.message || reponse?.error || 'Lecture impossible');
      setDonnees(reponse.data);
      if (reponse.data?.devenir?.annee) setAnneeChoisie(String(reponse.data.devenir.annee.id));
    } catch (erreur) {
      setEchec(erreur.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger(null);
  }, [charger]);

  const devenir = donnees?.devenir || null;
  const premierChargement = chargement && !donnees;

  return (
    <div>
      <FilArianeExamen
        codeCourant="RESULTATS_DEVENIR"
        libelleCourant="Résultats et devenir"
        onRetour={onRetour}
        onChangerExamen={onChangerExamen}
      />

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-[#246990]">Résultats et devenir</h2>
          <p className="text-sm text-gray-500">
            Le taux de réussite de l&apos;école, ce que sont devenus les candidats, et le taux de réinscription réel.
          </p>
        </div>
        {donnees?.annees?.length > 0 && (
          <div className="w-64">
            <p className="mb-1 text-xs text-gray-500">Examens de l&apos;année</p>
            <ChampSelect
              valeur={anneeChoisie}
              onChange={(valeur) => {
                setAnneeChoisie(valeur);
                charger(valeur);
              }}
              options={donnees.annees.map((a) => ({ valeur: String(a.id), libelle: a.nom }))}
              desactive={chargement}
              className="w-full"
            />
          </div>
        )}
      </div>

      {premierChargement ? (
        <LoadingSpinner message="Calcul du devenir des candidats…" />
      ) : echec && !donnees ? (
        <Card>
          <EtatVide
            icone={GraduationCap}
            titre="Le devenir des candidats n'a pas pu être calculé"
            description={echec}
            action={<Button variant="outline" size="sm" onClick={() => charger(anneeChoisie)}>Réessayer</Button>}
          />
        </Card>
      ) : donnees && !donnees.disponible ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          L&apos;enregistrement des résultats n&apos;est pas encore installé dans cette école.
        </div>
      ) : !devenir ? (
        <Card>
          <EtatVide
            icone={GraduationCap}
            titre="Aucun résultat d'examen pour l'instant"
            description="Saisissez les résultats, ou importez la liste officielle, depuis la vue Résultats de chaque examen."
          />
        </Card>
      ) : (
        <>
          <BlocReinscription
            reinscription={devenir.reinscription}
            annee={devenir.annee}
            suivante={devenir.annee_suivante}
            examenFinal={devenir.examen_final || 'BAC'}
          />
          {devenir.examens.map((examen) => (
            <BlocExamen
              key={`${devenir.annee.id}-${examen.code}`}
              examen={examen}
              suivante={devenir.annee_suivante}
              examenFinal={devenir.examen_final || 'BAC'}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default ResultatsDevenir;
